/* @flow */

import qs from 'qs';
import lzString from 'lz-string';

import { isIE } from 'utils/browserDetection';

import type { LayoutType, URLFilterItem, SortSpec } from './types';

const URL_MAX_LENGTH_IE = 2000;
const STATE_KEY = 'rs';
const QUERY_KEY_MAP = {
  page: 'p',
  searchTerms: 'st',

  filters: 'fs',
  key: 'k',
  values: 'vs',
  fixed: 'fxd',
  overrideImplicit: 'orImp',

  userSearchPreference: 'uPref',

  sort: 's',
  columnName: 'col',
  direction: 'dir',

  layout: 'l',
  tab: 't',
  lengthWarning: 'wLen',

  summary: 'sum',
  expanded: 'ex',
  region: 'rgn',
};

const STATE_KEY_MAP = Object.entries(QUERY_KEY_MAP).reduce(
  (result, [key, value]) => ({ ...result, [value]: key }),
  {}
);

export type ReportViewState = {
  page: number,
  searchTerm?: string,
  filters?: URLFilterItem[],
  /**
   * The ID of the user search preference applied
   */
  userSearchPreference?: string,
  sort?: SortSpec,
  layout?: LayoutType,
  tab?: string,
  /**
   * This property is set if the encoder finds that the resulting length of the query is too long for the client. It will have truncated the filters in order to adhere to the client limits. At the moment, this is only protected on IE.
   */
  lengthWarning?: boolean,
  summary?: {
    expanded?: boolean,
    region?: string,
  },
};

const mapObjectKeys = (obj: any, keyMap: { [string]: string }): any => {
  if (typeof obj === 'object' && obj !== null) {
    if ('map' in obj) {
      return obj.map(member => mapObjectKeys(member, keyMap));
    }

    return Object.entries(obj).reduce(
      (_result, [key, value]) => ({
        ..._result,
        [keyMap[key] || key]: mapObjectKeys(value, keyMap),
      }),
      {}
    );
  }

  return obj;
};

const mapToQueryKeys = (reportViewState: ReportViewState) =>
  mapObjectKeys(reportViewState, QUERY_KEY_MAP);
const mapFromQueryKeys = (obj: any): ReportViewState => mapObjectKeys(obj, STATE_KEY_MAP);

const compressState = (reportViewState: ReportViewState) =>
  lzString.compressToEncodedURIComponent(JSON.stringify(mapToQueryKeys(reportViewState)));

const decompressState = (stateString: string): ReportViewState =>
  mapFromQueryKeys(JSON.parse(lzString.decompressFromEncodedURIComponent(stateString)));

export const encodeStateUri = (reportViewState: ReportViewState): string =>
  qs.stringify({ [STATE_KEY]: compressState(reportViewState) });

export const decodeStateUri = (urlQuery: string): ReportViewState => {
  try {
    const _urlQuery = urlQuery[0] === '?' ? urlQuery.substring(1) : urlQuery;
    const queryObj = qs.parse(_urlQuery);

    if (queryObj && STATE_KEY in queryObj) {
      return decompressState(queryObj[STATE_KEY]);
    }

    throw new Error(NO_STATE_ERROR);
  } catch (error) {
    if (error.message !== NO_STATE_ERROR) {
      console.error('Error decoding the Report View State.', error);
    }

    return {
      page: 1,
    };
  }
};

const _formulateUri = (path: string, prefix: string, reportViewState: ReportViewState) =>
  `${path}${prefix}${encodeStateUri(reportViewState)}`;

export const formulateUri = (path: string, reportViewState: ReportViewState) => {
  const _reportViewState = { ...reportViewState };
  const prefix = path.includes('?') ? '&' : '?';
  let result = _formulateUri(path, prefix, _reportViewState);

  let watchdog = false;

  if (isIE) {
    // Attempt to shorten the result by removing filter item values one at a time
    while (new Blob([result]).size >= URL_MAX_LENGTH_IE && !watchdog) {
      watchdog = true;

      if (!_reportViewState.lengthWarning) {
        _reportViewState.lengthWarning = true;
        watchdog = false;
      }

      if (_reportViewState.filters) {
        const targetIndex = _reportViewState.filters.length - 1;
        if (targetIndex < 0) {
          delete _reportViewState.filters;
          watchdog = false;
        } else {
          const targetFilterItem = _reportViewState.filters[targetIndex];

          if (targetFilterItem.values.length) {
            targetFilterItem.values.splice(targetFilterItem.values.length - 1, 1);
            watchdog = false;
          } else {
            _reportViewState.filters.splice(targetIndex, 1);
            watchdog = false;
          }
        }
      }

      result = _formulateUri(path, prefix, _reportViewState);
    }
  }

  return result;
};

const NO_STATE_ERROR = 'No state found in the URI';
